import { ACOUSTIC_DEFAULTS } from './config';
import { SpeechModeAcousticEngine } from './engine';
import { averageSignals } from './calibrationSnapshot';
import type { AcousticRoomSignal } from '../liveLessonTypes';
import { mean } from './utils';

const TARGET_RATE = ACOUSTIC_DEFAULTS.targetSampleRate;

function rms(frame: Float32Array): number {
  if (!frame.length) return 0;
  let sum = 0;
  for (let i = 0; i < frame.length; i += 1) sum += frame[i] * frame[i];
  return Math.sqrt(sum / frame.length);
}

export function resampleTo16kMono(channelData: Float32Array, sourceRate: number): Float32Array {
  if (sourceRate === TARGET_RATE) return channelData;
  const targetLength = Math.max(1, Math.round((channelData.length * TARGET_RATE) / sourceRate));
  const out = new Float32Array(targetLength);
  const maxIndex = channelData.length - 1;
  for (let i = 0; i < targetLength; i += 1) {
    const sourcePos = (i / Math.max(1, targetLength - 1)) * maxIndex;
    const left = Math.floor(sourcePos);
    const right = Math.min(maxIndex, left + 1);
    const alpha = sourcePos - left;
    out[i] = channelData[left] * (1 - alpha) + channelData[right] * alpha;
  }
  return out;
}

export async function decodeAudioFileToMono16k(file: File): Promise<Float32Array> {
  const buffer = await file.arrayBuffer();
  const ctx = new AudioContext();
  try {
    const decoded = await ctx.decodeAudioData(buffer.slice(0));
    const channel = decoded.numberOfChannels > 1
      ? mixToMono(decoded)
      : decoded.getChannelData(0);
    return resampleTo16kMono(Float32Array.from(channel), decoded.sampleRate);
  } finally {
    await ctx.close();
  }
}

function mixToMono(buffer: AudioBuffer): Float32Array {
  const length = buffer.length;
  const out = new Float32Array(length);
  for (let ch = 0; ch < buffer.numberOfChannels; ch += 1) {
    const data = buffer.getChannelData(ch);
    for (let i = 0; i < length; i += 1) out[i] += data[i];
  }
  const scale = 1 / buffer.numberOfChannels;
  for (let i = 0; i < length; i += 1) out[i] *= scale;
  return out;
}

function frameIsSpeech(frame: Float32Array): boolean {
  return rms(frame) >= ACOUSTIC_DEFAULTS.rmsSilenceGate;
}

export type OfflineAnalysisResult = {
  fileName: string;
  durationSec: number;
  frameCount: number;
  signals: AcousticRoomSignal[];
  aggregate: AcousticRoomSignal;
};

export async function analyzeOfflineAudioSamples(
  samples: Float32Array,
  fileName: string,
  onProgress?: (ratio: number) => void
): Promise<OfflineAnalysisResult> {
  const engine = new SpeechModeAcousticEngine();
  await engine.initialize();
  engine.reset();

  if (samples.length >= ACOUSTIC_DEFAULTS.yamnetWindowSamples) {
    try {
      await engine.processSpeechSegment(samples.slice(0, ACOUSTIC_DEFAULTS.yamnetWindowSamples));
    } catch {
      // YAMNet optional
    }
  }

  const hop = ACOUSTIC_DEFAULTS.vadFrameSamples;
  const frameCount = Math.max(0, Math.floor((samples.length - hop) / hop));
  const signals: AcousticRoomSignal[] = [];
  for (let index = 0; index < frameCount; index += 1) {
    const offset = index * hop;
    const frame = samples.slice(offset, offset + hop);
    const isSpeech = frameIsSpeech(frame);
    const signal = engine.processVadFrame(isSpeech, frame);
    signals.push(signal);

    if (index > 0 && index % 40 === 0 && samples.length >= ACOUSTIC_DEFAULTS.yamnetWindowSamples) {
      const yamnetStart = Math.max(0, offset - ACOUSTIC_DEFAULTS.yamnetWindowSamples);
      void engine.processSpeechSegment(samples.slice(yamnetStart, yamnetStart + ACOUSTIC_DEFAULTS.yamnetWindowSamples));
    }

    if (onProgress && index % 8 === 0) {
      onProgress(offset / Math.max(1, samples.length - hop));
    }
  }

  if (samples.length >= hop) {
    const tail = samples.slice(-Math.min(samples.length, ACOUSTIC_DEFAULTS.yamnetWindowSamples));
    await engine.processSpeechSegment(tail);
    const lastFrame = samples.slice(samples.length - hop);
    signals.push(engine.processVadFrame(frameIsSpeech(lastFrame), lastFrame));
  }

  onProgress?.(1);

  const aggregate = averageSignals(signals.length ? signals : []) ?? signals[signals.length - 1];
  if (!aggregate) {
    throw new Error('Audiodatei enthält zu wenig Daten für die Analyse.');
  }

  return {
    fileName,
    durationSec: samples.length / TARGET_RATE,
    frameCount: signals.length,
    signals,
    aggregate
  };
}

export async function analyzeOfflineAudioFile(
  file: File,
  onProgress?: (ratio: number) => void
): Promise<OfflineAnalysisResult> {
  const samples = await decodeAudioFileToMono16k(file);
  return analyzeOfflineAudioSamples(samples, file.name, onProgress);
}

export function summarizeOfflineLevels(signals: AcousticRoomSignal[]): { avgLevelDb: number; avgLoudness: number } {
  return {
    avgLevelDb: mean(signals.map((s) => s.levelDb)),
    avgLoudness: mean(signals.map((s) => s.loudnessNormalized))
  };
}
